/**
 * PATH: packages/ui/src/qa/qaPhotoView.ts
 * FILE_ID: FE-QA-PHOTO-VIEW-001
 * PURPOSE: Hook-free size/Fit/pan merge for QA tiles, print HTML, and packs.
 * LANDMINE: Keys are evidence ids, never asset ids. Print and pack must
 *      resolve with the same id QaGalleryPhoto.id already uses.
 */

export const QA_PHOTO_SIZES = [72, 96, 128] as const
export type QaPhotoSize = (typeof QA_PHOTO_SIZES)[number]
export const QA_PHOTO_SIZE_LABEL: Record<QaPhotoSize, string> = {
  72: 'Small',
  96: 'Medium',
  128: 'Large',
}
export const QA_PHOTO_DEFAULT_SIZE: QaPhotoSize = 72
export const QA_PHOTO_DEFAULT_FIT = true
export const QA_PHOTO_LS_PX = 'qa.evidence.thumb.px'
export const QA_PHOTO_LS_FIT = 'qa.evidence.thumb.fit'
export const QA_PHOTO_LS_VIEWS = 'qa.evidence.photo.view'

export interface QaGalleryPhoto {
  id: string
  src: string | null
  alt?: string
  caption?: string
}

export interface QaPhotoView {
  size: QaPhotoSize
  fit: boolean
  panX: number
  panY: number
}

export interface QaPhotoViewSnapshot {
  defaultSize: QaPhotoSize
  defaultFit: boolean
  overrides: Record<string, Partial<QaPhotoView>>
}

export function nearestQaPhotoSize(px: number): QaPhotoSize {
  return QA_PHOTO_SIZES.reduce((best, n) =>
    Math.abs(n - px) < Math.abs(best - px) ? n : best,
  )
}

export function resolveQaPhotoView(id: string, snapshot: QaPhotoViewSnapshot): QaPhotoView {
  const over = snapshot.overrides[id] ?? {}
  return {
    size: over.size ?? snapshot.defaultSize,
    fit: over.fit ?? snapshot.defaultFit,
    panX: over.panX ?? 50,
    panY: over.panY ?? 50,
  }
}

function readStoredNumber(key: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (raw == null) return fallback
    const n = Number(raw)
    return Number.isFinite(n) ? n : fallback
  } catch {
    return fallback
  }
}

function readStoredBool(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (raw == null) return fallback
    return raw === '1' || raw === 'true'
  } catch {
    return fallback
  }
}

function readStoredOverrides(key: string): Record<string, Partial<QaPhotoView>> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(key)
    if (raw == null) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as Record<string, Partial<QaPhotoView>>
  } catch {
    return {}
  }
}

/** Read the three localStorage keys. Print and Save to Reports use this. */
export function snapshotQaPhotoViews(): QaPhotoViewSnapshot {
  return {
    defaultSize: nearestQaPhotoSize(readStoredNumber(QA_PHOTO_LS_PX, QA_PHOTO_DEFAULT_SIZE)),
    defaultFit: readStoredBool(QA_PHOTO_LS_FIT, QA_PHOTO_DEFAULT_FIT),
    overrides: readStoredOverrides(QA_PHOTO_LS_VIEWS),
  }
}
